/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["store"] }] */
/* global __BUILD_MODE__ __BOLT_ON_MODULES__ */
/*
   This script contains the functions that deal with store management and 
   creation.
   
   The main reducer is made up of the individual reducers for each of the 
   modules in the core of the application.
   
   The create register function returns a function that allows replacement
   of the current reducer with one that contains the new module being lazy 
   loaded in.
   
*/

import {
  createStateSyncMiddleware,
  initMessageListener,
} from "redux-state-sync"
import { createStore, applyMiddleware, combineReducers } from "redux"
import thunkMiddleware from "redux-thunk"
import { createLogger } from "redux-logger"
import { composeWithDevTools } from "redux-devtools-extension"
import { api } from "common/utils"
import { PRODUCTION } from "ksc-portal-shareable-configs/dist/webpack/webpack-foundation/constants"
import {
  SIGN_USER_OUT,
  STORE_SESSION_TIMER_INFO,
} from "../../client/src/CoreModules/SessionManagement/constants"
import coreReducers from "./coreReducers"

const actionPrefixes = new Set([
  "FOUNDATION",
  "foundation",
  "&",
  ...Object.values(__BOLT_ON_MODULES__)
    .map((el) => [el, el.toUpperCase()])
    .reduce((acc, curr) => [...acc, ...curr], []),
])

const customMiddleware = () => (next) => (action) => {
  // Our middleware
  const { type } = action
  if (!actionPrefixes.has(type.split("_")[0])) {
    console.error(
      `action ${type} is not correctly prefixed. Must have one of the following prefixes ${Array.from(
        actionPrefixes
      ).join(" ")} `
    )
    return
  }
  next(action)
}

const createRootReducer = (reducers) => {
  const appReducer = combineReducers(reducers)

  const rootReducer = (state, action) => {
    if (action.type === SIGN_USER_OUT) {
      // rootReducer used to reset redux state on sign out.
      return appReducer(undefined, action)
    }

    return appReducer(state, action)
  }

  return rootReducer
}

const createReducer = (reducers) => {
  const rootReducer = createRootReducer({
    ...coreReducers,
    ...reducers,
  })

  return rootReducer
}

const createRegister = (store) => {
  const modules = {}
  const settings = {}

  return (name, moduleProvider, proxy = false, settingsComponent = false) => {
    const cache = settingsComponent ? settings : modules
    if (Object.prototype.hasOwnProperty.call(cache, name))
      return Promise.resolve({ name, ...cache[name] })

    if (proxy)
      return new Promise((resolve) => {
        moduleProvider
          .then((pro) => {
            pro.default.then((mod) => {
              const { checkPermissionsObject } = mod
              cache[name] = mod
              api
                .load(name, checkPermissionsObject)
                .then(() => {
                  if (
                    !Object.prototype.hasOwnProperty.call(
                      store.getState(),
                      name
                    )
                  ) {
                    const { Reducer } = mod
                    store.async[name] = Reducer
                    store.replaceReducer(createReducer(store.async))
                  }
                })
                .then(() => resolve({ name, ...mod }))
                .catch(console.error)
            })
          })
          .catch(console.error)
      })

    return moduleProvider
      .then((mod) => {
        cache[name] = mod
        const { Reducer } = mod
        if (Array.isArray(Reducer)) {
          for (let i = 0; i < Reducer.length; i += 1) {
            store.async[Reducer[i].name] = Reducer[i].reducer
          }
        } else {
          store.async[name] = mod.Reducer
        }
        store.replaceReducer(createReducer(store.async))
        return { name, ...mod }
      })
      .catch(console.error)
  }
}

export default () => {
  const buildMode = Object.values(__BUILD_MODE__)[0]
  const middleware = [
    customMiddleware,
    thunkMiddleware,
    createStateSyncMiddleware({
      whitelist: [STORE_SESSION_TIMER_INFO, SIGN_USER_OUT],
    }),
  ]
  if (buildMode !== PRODUCTION)
    middleware.push(createLogger({ collapsed: true }))

  const store = createStore(
    combineReducers(coreReducers),
    composeWithDevTools(applyMiddleware(...middleware))
  )

  store.async = {}

  const register = createRegister(store)
  api.initStore(store)
  initMessageListener(store)
  return { store, register }
}
